import type { overview as en } from '@/i18n/locales/en/overview';
import type { Translation } from '@/i18n/types';

export const overview: Translation<typeof en> = {
  page: {
    title: 'Genel Bakış',
    lead: '{{company}} nasıl gidiyor?',
    noScope: 'Genel bakışı görmek için bir şirket seçin.',
    noDepartments: 'Buradaki hiçbir departmanın görebileceğiniz istatistikleri yok.',
    disclaimer:
      'Tüm değerler grup düzeyinde gösterge niteliğinde iyi oluş bilgileridir ve tıbbi teşhis olarak değerlendirilmemelidir.',
  },
  summary: {
    caption: 'Anonimleştirilmiş, birleştirilmiş verilere dayalı yapay zeka özeti.',
  },
  figures: {
    vitality: 'Canlılık',
    stress: 'Stres',
    sleep: 'Uyku',
    engagement: 'Katılım',
    activeParticipants: 'Aktif katılımcılar',
  },
  figuresInfo: {
    vitality: 'Uyku, aktivite, HRV, stres ve kalp atış hızına dayalı birleşik bir puan.',
    stress: 'Giyilebilir cihaz tarafından doğrudan ölçülen ortalama stres düzeyi.',
    sleep: 'Giyilebilir cihaz tarafından doğrudan ölçülen gece başına ortalama uyku süresi.',
    engagement: 'Uygulamayı aktif olarak kullanan çalışan oranı.',
    activeParticipants: 'Son dönemde aktif olan çalışan sayısı.',
  },
  legend: {
    title: 'Gördükleriniz',
    measured:
      'Doğrudan ölçülen — doğrudan NOLATR giyilebilir cihazından veya bağlı bir sensörden. En yüksek güvenilirlik.',
    aiDerived:
      'Yapay zeka ile türetilmiş — doğrudan bir ölçüm değil. Yapay zeka birden çok sinyali bir göstergede birleştirir.',
    usage: 'Kullanım & davranış — uygulamadaki gerçek etkileşimler, bir sağlık ölçümü değil.',
  },
  developments: {
    title: 'Önemli gelişmeler',
    empty: 'Bu dönemde dikkat çekici bir gelişme yok.',
    stress: {
      title: 'Dinlenme fırsatı sunma zamanı (stres yüksek)',
      body: 'Dostane bir dinlenme anı veya nefes egzersizi ipucu, ekibin enerji toplamasına yardımcı olabilir.',
    },
    sleep: {
      title: 'Fırsat: uyku hedefin altında',
      body: 'Hedefe yönelik, olumlu bir toparlanma ipucu ekibi iyi destekleyebilir.',
    },
    vitalityGood: {
      title: 'Canlılık iyi yönde gelişiyor',
      body: 'Ekip mevcut ritmini iyi koruyor — bunu takdir etmek için güzel bir an.',
    },
    needsReactivation: {
      title: '{{count}} kişi yeniden etkinleştirilmeyi bekliyor',
      body: 'Kısa, kişisel bir yeniden etkinleştirme mesajı genellikle en iyi sonucu verir.',
    },
    mostActiveWeekday: {
      title: 'En çok enerji: {{weekday}}',
      body: 'Maksimum erişim için meydan okumaları ve iletişimi en aktif güne göre planlayın.',
    },
    challengeParticipation: {
      title: 'Meydan okuma katılımı {{pct}}%',
      body: 'Düşük eşikli yeni bir ekip meydan okuması daha fazla kişiyi katılıma teşvik edebilir.',
    },
  },
  actions: {
    title: 'İK için eylemler',
    empty: 'Şu anda önerilen bir eylem yok.',
    sendReminder: {
      title: '{{count}} çalışan henüz etkinleştirilmedi',
      subtitle: 'Bir hatırlatma gönderin',
    },
    reactivate: {
      title: '{{count}} çalışan yeniden etkinleştirilmeyi bekliyor',
      subtitle: 'İletişime geçin',
    },
    stressCheckIn: {
      title: 'Ekipte stres seviyesi yüksek',
      subtitle: 'Kısa bir iş yükü değerlendirmesi düşünün',
    },
    sleepReminder: {
      title: 'Ekipte uyku hedefin altında',
      subtitle: 'Uyumadan önceki rutinler ve ekran süresi hakkında bir hatırlatma düşünün',
    },
    activeChallenge: {
      title: "'{{name}}' devam ediyor — bir sonrakini planlayın",
      subtitle: 'Meydan okumalar',
    },
    pilotEvaluation: {
      interim: {
        title: "'{{name}}' pilotu ara değerlendirmeye hazır",
        subtitle: 'Pilotu görüntüle',
      },
      final: {
        title: "'{{name}}' pilotu nihai değerlendirmeye hazır",
        subtitle: 'Pilotu görüntüle',
      },
    },
  },
  energy: {
    title: 'Enerji ve çalışma refahı',
    subtitle:
      'Ekibin iş gününü nasıl deneyimlediği — enerji, dinlenme araları, çalışma ritmi ve katılım. Sadece toplu, asla bireysel.',
    badge: 'Toplu ve anonimleştirilmiş',
    tbd: 'Belirlenecek',
    missingData: {
      noSource: 'Enerji endeksi ve eNPS: veri kaynağı henüz belirlenmedi.',
      insufficientParticipants: '{{metrics}} için yeterli katılımcı yok.',
    },
    privacyNote:
      'Destekleyici içgörüler, izleme değil. Grup düzeyinde gösterge niteliğinde refah — tıbbi yorum içermez.',
    metrics: {
      energyIndex: 'Enerji endeksi',
      breakAdoption: 'Ara benimseme',
      focusRhythm: 'Odak ritmi',
      coachAdoption: 'Koç benimseme',
      enps: 'eNPS',
    },
  },
  pilots: {
    title: 'Pilot durumu',
    empty: 'Henüz pilot yok.',
    participantsAndDay: '{{count}} katılımcı · gün {{day}} / {{total}}',
    viewReport: 'Pilot raporunu görüntüle',
  },
  challenges: {
    title: 'Aktif meydan okumalar',
    empty: 'Henüz meydan okuma yok.',
    active: 'Aktif',
    total: 'Toplam',
    participants: 'Katılımcılar',
    activeBadge: '{{count}} aktif',
    manageLink: 'Meydan okumaları yönet',
    noneActive: 'Şu anda aktif meydan okuma yok.',
    participantsCount: '{{count}} katılımcı',
  },
  trends: {
    title: 'Eğilimler',
    filters: {
      allDepartments: 'Tüm departmanlar',
      participants: {
        label: 'Katılımcılar',
        all: 'Tüm katılımcılar',
        manager: 'Yöneticiler',
        employee: 'Çalışanlar',
      },
    },
    vitalityScore: 'Zaman içinde canlılık puanı',
    activeUsersPerWeek: 'Haftalık aktif kullanıcılar',
    sleep: 'Uyku eğilimi',
    stress: 'Stres eğilimi',
    hrv: 'HRV eğilimi',
    recovery: 'Toparlanma eğilimi',
    activity: 'Aktivite eğilimi',
    notAvailable: 'Bu eğilim için henüz veri kaynağı yok — bu sürümde mevcut değil.',
    vsPreviousPeriod: 'önceki döneme göre',
    info: {
      vitalityScore: 'Tüm canlılık sinyallerine dayalı, güne göre birleşik bir canlılık puanı.',
      activeUsersPerWeek: 'Uygulamayı her hafta aktif olarak kullanan benzersiz çalışan sayısı.',
      sleep: 'Giyilebilir cihaz tarafından doğrudan ölçülen güne göre ortalama uyku süresi.',
      stress: 'Giyilebilir cihaz tarafından doğrudan ölçülen güne göre ortalama stres düzeyi.',
      hrv: 'Toparlanmanın bir göstergesi olan, güne göre ortalama kalp atış hızı değişkenliği (HRV).',
      recovery: 'Uyku, HRV ve aktiviteye dayalı, güne göre birleşik bir toparlanma puanı.',
      activity: 'Giyilebilir cihaz tarafından doğrudan ölçülen güne göre ortalama adım sayısı.',
    },
  },
};
